/**
 * DashboardPage - Projects Dashboard using ModernLayout
 * 
 * Layout: Uses ModernLayout which provides consistent left/right margins and sidebar.
 * Fetches project and client data from Redux and renders the modernized ProjectDashboard.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { func, array } from 'prop-types';
import { Spin, Form, notification, Modal, DatePicker, Select, Input, InputNumber, Row, Col } from 'antd';
import { ArrowLeftOutlined, CloseOutlined, PlusOutlined, CheckOutlined } from '@ant-design/icons';
import { Button as CrceButton } from '@crc-evans/ui-components';
import get from 'lodash/get';
import moment from 'moment';
import { fetchProject, submitProject, updateProject } from '../../redux/actions/projectAction';
import { fetchClients } from '../../redux/actions/clientAction';
import { fetchStationDetails } from '../../redux/stationDetails/action';
import { fetchTestTypes } from '../../redux/testDetails/action';
import { fetchEquipList, fetchTeamMembersAccessForProject } from '../../redux/pihEquipmentDetails/action';
import { getCurrentUser } from '../../sagas/auth';
import { signOut } from '../../redux/actions/authActions';
import CreateProject from '../../components/createProject';
import { NDT_PIPELINE_TYPE_OPTIONS } from '../../constants/ndtPipelineTypeOptions';
import { ndtTypeFormInitial, normalizeNdtTypeForApi } from '../../utils/sowNdtStation';
import {
  buildProjectConfigExtras,
  formatProjectListDate,
  getProjectDisplayFields,
  getSiteLocationLabel,
  mergeProjectConfigExtras,
  parseProjectConfig,
  resolveProjectPihRefId,
} from '../../utils/projectConfig';
import ModernMultiCheckboxSelect from '../../components/common/ModernMultiCheckboxSelect';
import TabList from '../../layout/tab';
import ProjectDetailsModal from '../../components/ProjectDetailsModal';
import {
  Search,
  Plus,
  MapPin,
  User,
  Calendar,
  Edit3,
  Pencil,
  BarChart3,
  Flame,
  Rocket,
  Building2,
  PauseCircle,
  CheckCircle2,
  FolderOpen,
  Hash,
  Users,
  ChevronLeft,
} from 'lucide-react';
import './DashboardPage.css';
import '../technical/HeatProfilePage.css';
import '../../styles/ProjectUiSpin.css';
import { useHeatStyleColumnFacets } from '../../components/technical/heatStyleColumnFacets';

/**
 * One triangle at a time: muted up when idle, solid up (asc) or solid down (desc) when active.
 */
const SortTriangleToggle = ({ direction }) => {
  const isDesc = direction === 'desc';
  const isAsc = direction === 'asc';
  return (
    <span className="dashboard-th-sort-tri-single" aria-hidden>
      {isDesc ? (
        <svg
          className="dashboard-th-sort-tri dashboard-th-sort-tri--down is-on"
          width="10"
          height="5"
          viewBox="0 0 10 5"
          focusable="false"
        >
          <polygon points="0,0 10,0 5,5" fill="currentColor" />
        </svg>
      ) : (
        <svg
          className={`dashboard-th-sort-tri dashboard-th-sort-tri--up ${isAsc ? 'is-on' : 'is-idle'}`}
          width="10"
          height="5"
          viewBox="0 0 10 5"
          focusable="false"
        >
          <polygon points="5,0 0,5 10,5" fill="currentColor" />
        </svg>
      )}
    </span>
  );
};

const statusColors = {
  Completed: { bg: '#ECFDF5', text: '#059669', border: '#10B981' },
  Active: { bg: '#FFF7ED', text: '#EA580C', border: '#F97316' },
  OnHold: { bg: '#EFF6FF', text: '#2563EB', border: '#3B82F6' },
  Initiated: { bg: '#EEF2FF', text: '#4F46E5', border: '#6366F1' },
  Facility: { bg: '#FDF2F8', text: '#DB2777', border: '#EC4899' },
  Unknown: { bg: '#F3F4F6', text: '#6B7280', border: '#9CA3AF' },
};

/** projectStatus value for Active projects (default dashboard filter). */
const PROJECT_STATUS_ACTIVE = 2;

const getStatusString = (status) => {
  switch (status) {
    case 1: return 'Initiated';
    case 2: return 'Active';
    case 3: return 'OnHold';
    case 4: return 'Completed';
    case 5: return 'Facility';
    default: return 'Unknown';
  }
};

const getProjectTypeLabelStatic = (p) => (p && (p.projectType ?? p.project_type)) || '';

/** Display string for column filter / unique list (must match row rendering) */
const getCellFilterDisplay = (project, columnKey) => {
  if (!project) return '—';
  switch (columnKey) {
    case 'projectStatus':
      return getStatusString(project.projectStatus);
    case 'projectType':
      return getProjectTypeLabelStatic(project) || '—';
    case 'startDate':
      return formatProjectListDate(project.startDate);
    case 'endDate':
      return formatProjectListDate(project.endDate);
    case 'projectName':
      return project.projectName || '—';
    case 'location':
      return project.location || '—';
    case 'projectId':
      return project.projectId || '—';
    case 'clientName':
      return project.clientName || '—';
    case 'manager':
      return project.manager || '—';
    default:
      return String(project[columnKey] ?? '') || '—';
  }
};

/** Heat-style facet keys on rows enriched with __statusLabel, __startDisplay, __endDisplay */
const DASHBOARD_FACET_KEYS = [
  'projectName',
  '__statusLabel',
  'location',
  'projectType',
  'projectId',
  'clientName',
  'manager',
  '__startDisplay',
  '__endDisplay',
];

const DASHBOARD_HEAT_FILTERS_EMPTY = {
  projectName: '',
  __statusLabel: '',
  location: '',
  projectType: '',
  projectId: '',
  clientName: '',
  manager: '',
  __startDisplay: '',
  __endDisplay: '',
};

const getStatusIcon = (status) => {
  switch (status) {
    case 0: return <BarChart3 size={18} />;
    case 2: return <Flame size={18} />;
    case 1: return <Rocket size={18} />;
    case 5: return <Building2 size={18} />;
    case 3: return <PauseCircle size={18} />;
    case 4: return <CheckCircle2 size={18} />;
    default: return <BarChart3 size={18} />;
  }
};

const DashboardPage = (props) => {
  const {
    onGetProjects,
    projectData,
    showProjectLoader,
    onGetClients,
    clientData,
    onSubmitProject,
    onUpdateProject,
    isAuthenticated,
    onSignOut,
    onGetStations,
    stationData,
    onGetTestValueTypes,
    testTypes,
    onFetchEquipList,
    equipList,
    onFetchTeamMembersAccessForProject,
    projectTeamMembersAccessList,
  } = props;

  const history = useHistory();
  const [projectList, setProjectList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [heatColumnFilters, setHeatColumnFilters] = useState(() => ({ ...DASHBOARD_HEAT_FILTERS_EMPTY }));
  const [filterStatus, setFilterStatus] = useState(PROJECT_STATUS_ACTIVE);
  const [listSelectedProjectId, setListSelectedProjectId] = useState(null);
  const initialListSelectionDone = useRef(false);
  const [completedProject, setCompletedProject] = useState(0);
  const [facilityProject, setFacilityProject] = useState(0);
  const [activeProject, setActiveProject] = useState(0);
  const [initiatedProject, setInitiatedProject] = useState(0);
  const [onHoldProject, setOnHoldProject] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [projectForm] = Form.useForm();

  // Project detail view state (same as Home page)
  const [selectedProject, setSelectedProj] = useState('');
  const [isShowProjectData, setIsShowProjectData] = useState(false);
  const [stationList, setStationList] = useState([]);
  const [testTypesList, setTestTypesList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);

  // Edit modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [updateProjectId, setUpdateProjectId] = useState(0);
  const [projectName, setProjectName] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [startDateInput, setStartDateInput] = useState(null);
  const [endDateInput, setEndDateInput] = useState(null);
  const [dayShiftInputHour, setDayShiftInputHour] = useState('');
  const [nightShiftInputHour, setNightShiftInputHour] = useState('');
  const [editProjectType, setEditProjectType] = useState('');
  const [editNdtType, setEditNdtType] = useState(undefined);
  const [editFinalClient, setEditFinalClient] = useState('');
  const [editProjectSiteType, setEditProjectSiteType] = useState(undefined);

  const editSiteLocationLabel = getSiteLocationLabel(editProjectSiteType);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      onGetProjects();
      onGetClients();
      onGetStations();
      onGetTestValueTypes();
    }
  }, []);

  useEffect(() => {
    if (projectData) {
      const sorted = [...projectData].sort((a, b) => parseFloat(a.id) - parseFloat(b.id));
      setProjectList(sorted);
    }
  }, [projectData]);

  useEffect(() => {
    if (clientData) {
      setClientList([...clientData]);
    }
  }, [clientData]);

  useEffect(() => {
    if (stationData) {
      setStationList([...stationData]);
    }
  }, [stationData]);

  useEffect(() => {
    if (testTypes) {
      setTestTypesList([...testTypes]);
    }
  }, [testTypes]);

  useEffect(() => {
    if (equipList) {
      setEquipmentList([...equipList]);
    }
  }, [equipList]);

  useEffect(() => {
    if (selectedProject.id) {
      onFetchTeamMembersAccessForProject({ id: selectedProject.id });
      localStorage.setItem('selectedProject', JSON.stringify(selectedProject));
      sessionStorage.setItem('activeId', selectedProject.id);
    }
  }, [selectedProject.id, onFetchTeamMembersAccessForProject]);

  useEffect(() => {
    if (!projectList?.length) {
      setCompletedProject(0);
      setFacilityProject(0);
      setActiveProject(0);
      setInitiatedProject(0);
      setOnHoldProject(0);
      return;
    }
    let completedCount = 0;
    let activeCount = 0;
    let facilityCount = 0;
    let initiatedCount = 0;
    let onHoldCount = 0;
    projectList.forEach((item) => {
      switch (item.projectStatus) {
        case 5: facilityCount++; break;
        case 4: completedCount++; break;
        case 2: activeCount++; break;
        case 1: initiatedCount++; break;
        default: onHoldCount++; break;
      }
    });
    setCompletedProject(completedCount);
    setFacilityProject(facilityCount);
    setActiveProject(activeCount);
    setInitiatedProject(initiatedCount);
    setOnHoldProject(onHoldCount);
  }, [projectList]);

  useEffect(() => {
    if (!projectList?.length || initialListSelectionDone.current) return;
    initialListSelectionDone.current = true;

    const shouldReturnToDetail = sessionStorage.getItem('returnToProjectDetail') === 'true';
    if (shouldReturnToDetail) {
      sessionStorage.removeItem('returnToProjectDetail');
      const storedId = sessionStorage.getItem('activeId');
      if (storedId) {
        const parsed = parseInt(storedId, 10);
        const fromStorage = projectList.find((p) => p.id === parsed);
        if (fromStorage) {
          setListSelectedProjectId(fromStorage.id);
          setSelectedProj(fromStorage);
          sessionStorage.setItem('activeId', fromStorage.id);
          localStorage.setItem('selectedProject', JSON.stringify(fromStorage));
          setIsShowProjectData(true);
          return;
        }
      }
    }

    const activeProjects = projectList.filter((p) => p.projectStatus === PROJECT_STATUS_ACTIVE);
    const storedId = sessionStorage.getItem('activeId');
    let pick = null;

    if (storedId) {
      const parsed = parseInt(storedId, 10);
      const fromStorage = projectList.find((p) => p.id === parsed);
      if (fromStorage?.projectStatus === PROJECT_STATUS_ACTIVE) {
        pick = fromStorage;
      }
    }
    if (!pick && activeProjects.length > 0) {
      pick = activeProjects[0];
    }

    if (pick?.id != null) {
      setListSelectedProjectId(pick.id);
      setSelectedProj(pick);
      sessionStorage.setItem('activeId', pick.id);
      localStorage.setItem('selectedProject', JSON.stringify(pick));
    }
  }, [projectList]);

  const searchData = (e) => {
    setSearchText(e.target.value);
  };

  const showModal = (record) => {
    setUpdateProjectId(record.id);
    if (record?.id) {
      const cfg = parseProjectConfig(record.config);
      const display = getProjectDisplayFields(record);
      const dayH = cfg.technicalLogDefaultShiftHours?.day;
      const nightH = cfg.technicalLogDefaultShiftHours?.night;
      const pt = record?.projectType || '';
      const showNdtReject = pt === 'Welding' || pt === 'Both';
      projectForm.setFieldsValue({
        projectName: record?.projectName || '',
        projectId: record?.projectId || '',
        clientName: record?.clientName || '',
        manager: record?.manager || '',
        location: display.country,
        subcontractor: display.subcontractor,
        projectSiteType: display.projectSiteType || undefined,
        siteLocationDetail: display.siteLocationDetail,
        crceRepresentative: display.crceRepresentative,
        startDate: record?.startDate ? moment(record.startDate, 'MM-DD-YYYY') : null,
        endDate: record?.endDate ? moment(record.endDate, 'MM-DD-YYYY') : null,
        projectStatus: record?.projectStatus,
        completedWelds: '-',
        technicianLog: `${dayH ?? '0'} Hour / ${nightH ?? '0'} Hour`,
        projectType: pt || undefined,
        ndtType: ndtTypeFormInitial(record),
        ndtRejectMaxPercent:
          showNdtReject && cfg.ndtRejectMaxPercent != null && cfg.ndtRejectMaxPercent !== ''
            ? Number(cfg.ndtRejectMaxPercent)
            : undefined,
        finalClient: record?.finalClient || '',
      });
      setDayShiftInputHour(dayH);
      setNightShiftInputHour(nightH);
      setProjectName(record?.projectName);
      setEditProjectType(pt);
      setEditNdtType(ndtTypeFormInitial(record));
      setEditFinalClient(record?.finalClient || '');
      setEditProjectSiteType(display.projectSiteType || undefined);
      setIsModalVisible(true);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    projectForm.resetFields();
    setEditProjectType('');
    setEditNdtType(undefined);
    setEditFinalClient('');
    setEditProjectSiteType(undefined);
  };

  const handleSave = async () => {
    try {
      await projectForm.validateFields();
      const formValues = projectForm.getFieldsValue();
      const currentProject = projectList.find((p) => p.id === updateProjectId);
      const prevConfig = parseProjectConfig(currentProject?.config);
      const projectTypeVal = formValues.projectType ?? editProjectType ?? '';
      const weldingOrBoth =
        projectTypeVal === 'Welding' || projectTypeVal === 'Both';
      const ndtRejectRaw = formValues.ndtRejectMaxPercent;
      const ndtRejectNum =
        ndtRejectRaw !== undefined && ndtRejectRaw !== null && ndtRejectRaw !== ''
          ? Number(ndtRejectRaw)
          : undefined;

      const nextConfig = mergeProjectConfigExtras(prevConfig, {
        subcontractor: formValues.subcontractor,
        projectSiteType: formValues.projectSiteType ?? editProjectSiteType,
        siteLocationDetail: formValues.siteLocationDetail,
        crceRepresentative: formValues.crceRepresentative,
      });
      nextConfig.technicalLogDefaultShiftHours = {
        day: dayShiftInputHour?.toString() || '',
        night: nightShiftInputHour?.toString() || '',
      };
      if (
        weldingOrBoth &&
        ndtRejectNum !== undefined &&
        !Number.isNaN(ndtRejectNum)
      ) {
        nextConfig.ndtRejectMaxPercent = ndtRejectNum;
      } else {
        delete nextConfig.ndtRejectMaxPercent;
      }

      const updateData = {
        projectName: String(formValues.projectName ?? '').trim(),
        startDate: moment(formValues.startDate)?.format('MM-DD-YYYY'),
        endDate: moment(formValues.endDate)?.format('MM-DD-YYYY'),
        projectStatus: formValues.projectStatus,
        projectType: projectTypeVal,
        finalClient: formValues.finalClient ?? editFinalClient ?? '',
        location: formValues.location,
        contractor: formValues.clientName,
        ndtType:
          normalizeNdtTypeForApi(formValues.ndtType) ??
          normalizeNdtTypeForApi(editNdtType),
        config: nextConfig,
      };
      if (updateProjectId) {
        onUpdateProject({ data: updateData, id: updateProjectId });
        setIsModalVisible(false);
      } else {
        notification.warn("Project ID is missing, can't update project.");
      }
    } catch (error) {
      console.error('Validation failed:', error);
      notification.error('Please correct the form errors before saving.');
    }
  };

  const handleProjectClick = (record) => {
    sessionStorage.setItem('activeId', record.id);
    setListSelectedProjectId(record.id);
    setSelectedProj(record);
    localStorage.setItem('selectedProject', JSON.stringify(record));
    setIsShowProjectData(true);
  };

  const onSaveProject = async (data, client) => {
    try {
      if (data.endDate) {
        data.endDate = moment(data.endDate).format('MM-DD-YYYY');
      } else {
        data.endDate = null;
      }
      const resolvedClient =
        client && client.id
          ? client
          : (clientList || []).find((c) => c && c.name === data.clientName);
      if (!resolvedClient || !resolvedClient.id) {
        notification.warning({
          message: 'Please select a valid Client.',
        });
        return;
      }
      const finalClientTrimmed =
        data.finalClient != null && String(data.finalClient).trim() !== ''
          ? String(data.finalClient).trim()
          : undefined;
      const ptCreate = data.projectType ?? '';
      const weldingOrBothCreate =
        ptCreate === 'Welding' || ptCreate === 'Both';
      const ndtRejectRaw = data.ndtRejectMaxPercent;
      const ndtRejectNum =
        ndtRejectRaw !== undefined && ndtRejectRaw !== null && ndtRejectRaw !== ''
          ? Number(ndtRejectRaw)
          : undefined;
      const includeNdtRejectCreate =
        weldingOrBothCreate &&
        ndtRejectNum !== undefined &&
        !Number.isNaN(ndtRejectNum);
      const configExtras = buildProjectConfigExtras(data);
      const pihRefId = resolveProjectPihRefId(data);
      var obj = {
        projectId: data.projectId,
        projectName: data.projectName,
        manager: data.manager,
        startDate: moment(data.startDate).format('MM-DD-YYYY'),
        endDate: data.endDate,
        contractId: data.contractId,
        ...(pihRefId ? { pihRefId } : {}),
        contractor: data.contractor ?? data.clientName,
        location: data.location,
        projectType: data.projectType ?? '',
        ...(normalizeNdtTypeForApi(data.ndtType) != null
          ? { ndtType: normalizeNdtTypeForApi(data.ndtType) }
          : {}),
        clientId: resolvedClient.id,
        ...(finalClientTrimmed ? { finalClient: finalClientTrimmed } : {}),
        config: {
          technicalLogDefaultShiftHours: {
            day: String(data.dayShiftHour ?? 0),
            night: String(data.nightShiftHour ?? 0),
          },
          ...(includeNdtRejectCreate
            ? { ndtRejectMaxPercent: ndtRejectNum }
            : {}),
          ...configExtras,
        },
        teamMember: data.teamMember,
      };
      await onSubmitProject({ data: obj, clientName: data.clientName });
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getProjectTypeLabel = (p) => getProjectTypeLabelStatic(p);

  const filteredByStatusAndSearch = useMemo(() => {
    let rows = Array.isArray(projectList) ? [...projectList] : [];
    const text = searchText.trim().toLowerCase();

    if (text.length > 0) {
      rows = rows.filter(
        (value) =>
          value.projectName?.toLowerCase().includes(text) ||
          value.projectId?.toLowerCase().includes(text) ||
          value.location?.toLowerCase().includes(text) ||
          String(value.projectType || value.project_type || '')
            .toLowerCase()
            .includes(text) ||
          value.manager?.toLowerCase().includes(text) ||
          value.clientName?.toLowerCase().includes(text)
      );
      if (filterStatus > 0) {
        rows.sort((a, b) => {
          const aMatchesStatus = a.projectStatus === filterStatus ? 0 : 1;
          const bMatchesStatus = b.projectStatus === filterStatus ? 0 : 1;
          return aMatchesStatus - bMatchesStatus;
        });
      }
    } else if (filterStatus > 0) {
      rows = rows.filter((item) => item.projectStatus === filterStatus);
    }

    return rows;
  }, [projectList, filterStatus, searchText]);

  const facetEnrichedRows = useMemo(
    () =>
      (filteredByStatusAndSearch || []).map((r) => ({
        ...r,
        __statusLabel: getCellFilterDisplay(r, 'projectStatus'),
        __startDisplay: getCellFilterDisplay(r, 'startDate'),
        __endDisplay: getCellFilterDisplay(r, 'endDate'),
      })),
    [filteredByStatusAndSearch]
  );

  const handleHeatColumnFilterChange = useCallback((key, value) => {
    setHeatColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const dashboardFacetRef = useRef([]);
  dashboardFacetRef.current = facetEnrichedRows;
  const fetchDashboardFacetRows = useCallback(async () => {
    const list = dashboardFacetRef.current;
    return Array.isArray(list) ? list : [];
  }, []);

  const dashboardFacetSearchCriteria = useMemo(() => ({}), []);

  const { applyFacetClientFilter, renderTextColumnFilterTrigger } = useHeatStyleColumnFacets({
    enabled: true,
    filterableColumns: DASHBOARD_FACET_KEYS,
    listCount: facetEnrichedRows.length,
    projectList: facetEnrichedRows,
    searchCriteria: dashboardFacetSearchCriteria,
    columnFilters: heatColumnFilters,
    onColumnFilterChange: handleHeatColumnFilterChange,
    fetchFacetRows: fetchDashboardFacetRows,
  });

  const lc = (v) => (v == null ? '' : String(v)).trim().toLowerCase();

  const filteredByTextAndFacet = useMemo(() => {
    let list = [...facetEnrichedRows];
    DASHBOARD_FACET_KEYS.forEach((key) => {
      const raw = (heatColumnFilters[key] || '').trim();
      if (!raw) return;
      const q = lc(raw);
      list = list.filter((row) => lc(get(row, key)).includes(q));
    });
    return applyFacetClientFilter(list);
  }, [facetEnrichedRows, heatColumnFilters, applyFacetClientFilter]);

  const sortedDisplay = useMemo(() => {
    const rows = [...filteredByTextAndFacet];
    if (!sortConfig.key) return rows;
    const key = sortConfig.key;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const cmpStr = (a, b) => {
      const av = String(a ?? '').toLowerCase();
      const bv = String(b ?? '').toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    };
    const cmpNum = (a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (na < nb) return -1 * dir;
      if (na > nb) return 1 * dir;
      return 0;
    };
    rows.sort((a, b) => {
      if (key === 'projectStatus') {
        return cmpNum(a.projectStatus, b.projectStatus);
      }
      if (key === 'startDate' || key === 'endDate') {
        const ma = a[key] ? moment(a[key]).valueOf() : 0;
        const mb = b[key] ? moment(b[key]).valueOf() : 0;
        return cmpNum(ma, mb);
      }
      if (key === 'projectType') {
        return cmpStr(getProjectTypeLabelStatic(a), getProjectTypeLabelStatic(b));
      }
      return cmpStr(a[key] || '', b[key] || '');
    });
    return rows;
  }, [filteredByTextAndFacet, sortConfig]);

  const renderHeatColumnHeader = useCallback(
    (label, facetDataIndex, sortColKey, { filterable = true, sortable = true } = {}) => {
      const activeSort = sortConfig.key === sortColKey;
      const sortDirectionForCol = activeSort ? sortConfig.direction : null;
      return (
        <div className="dashboard-th-heat-wrap">
          <div className="dashboard-th-heat-top spares-column-header-sortable">
            <div className="dashboard-th-title-cluster">
              <span
                className="dashboard-th-label dashboard-th-label--heat"
                onClick={sortable ? () => handleSort(sortColKey) : undefined}
                role={sortable ? 'button' : undefined}
              >
                {label}
              </span>
              {sortable && (
                <button
                  type="button"
                  className={`dashboard-th-sort-btn dashboard-th-sort-btn--heat ${activeSort ? 'is-active' : ''}`}
                  aria-label={
                    activeSort
                      ? sortConfig.direction === 'desc'
                        ? `Sorted by ${label}, descending. Click for ascending.`
                        : `Sorted by ${label}, ascending. Click for descending.`
                      : `Sort by ${label}`
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSort(sortColKey);
                  }}
                >
                  <SortTriangleToggle direction={sortDirectionForCol} />
                </button>
              )}
              {filterable ? renderTextColumnFilterTrigger(facetDataIndex) : null}
            </div>
          </div>
        </div>
      );
    },
    [sortConfig, handleSort, renderTextColumnFilterTrigger]
  );

  const statCards = [
    { key: 0, label: 'Total Projects', count: projectList.length, icon: <BarChart3 size={20} />, color: '#6366F1' },
    { key: PROJECT_STATUS_ACTIVE, label: 'Active', count: activeProject, icon: <Flame size={20} />, color: '#F97316' },
    { key: 1, label: 'Initiated', count: initiatedProject, icon: <Rocket size={20} />, color: '#3B82F6' },
    { key: 5, label: 'Facility', count: facilityProject, icon: <Building2 size={20} />, color: '#EC4899' },
    { key: 3, label: 'On Hold', count: onHoldProject, icon: <PauseCircle size={20} />, color: '#06B6D4' },
    { key: 4, label: 'Completed', count: completedProject, icon: <CheckCircle2 size={20} />, color: '#10B981' },
  ];

  if (showProjectLoader) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-loading project-ui-spin">
          <Spin size="large" tip="Loading projects..." />
        </div>
      </div>
    );
  }

  // --- PROJECT DETAIL VIEW (same as original Home page) ---
  if (selectedProject.id && isShowProjectData) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-project-detail">
          <div className="dashboard-project-detail-header-row">
            <div className="dashboard-project-detail-titles">
              <h1 className="dashboard-project-detail-title">
                Project : {`${selectedProject.projectName} (${selectedProject.projectId})`}
              </h1>
              <p className="dashboard-project-detail-subtitle">
                Manage project details and monitoring setup.
              </p>
            </div>
            <div className="dashboard-project-detail-actions">
              <ProjectDetailsModal
                selectedProject={selectedProject}
                onUpdateProject={onUpdateProject}
                projectTeamMembersAccessList={projectTeamMembersAccessList}
              />
            </div>
          </div>
          <button
            type="button"
            className="dashboard-project-detail-back"
            onClick={() => setIsShowProjectData(false)}
          >
            <ChevronLeft size={20} aria-hidden />
            Back to projects
          </button>
        </div>
        <div className="dashboard-project-tab-shell project-ui-spin">
          <TabList
            selectedProject={selectedProject}
            stationList={stationList}
            testTypesList={testTypesList}
            setStationList={setStationList}
            equipmentList={equipmentList}
            projectTeamMembersAccessList={projectTeamMembersAccessList}
          />
        </div>
      </div>
    );
  }

  const sortedData = sortedDisplay;

  return (
    <div className="dashboard-page">
      {/* Page Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <div
            className="dashboard-header-icon"
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: '#f3e8ff',
              color: '#9333ea',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FolderOpen size={20} strokeWidth={2} />
          </div>
          <div className="dashboard-header-text">
            <h1 className="dashboard-title">Projects</h1>
            <p className="dashboard-subtitle">Manage and monitor all your projects in one place</p>
          </div>
        </div>
        <div className="dashboard-header-right">
          <CrceButton
            label="Add Project"
            variant="primary"
            size="medium"
            icon={Plus}
            onClick={() => {
              // Trigger CreateProject modal via hidden button
              const btn = document.querySelector('.dashboard-create-project-hidden .ant-btn');
              if (btn) btn.click();
            }}
          />
          <div className="dashboard-create-project-hidden">
            <CreateProject
              projectForm={projectForm}
              onSaveProject={onSaveProject}
              clientList={clientList}
            />
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="dashboard-stats-row">
        {statCards.map((stat) => (
          <div
            key={stat.key}
            className={`dashboard-stat-card ${filterStatus === stat.key ? 'active' : ''}`}
            onClick={() => setFilterStatus(stat.key)}
            style={{ '--stat-color': stat.color }}
          >
            <div className="dashboard-stat-icon" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
              {stat.icon}
            </div>
            <div className="dashboard-stat-info">
              <span className="dashboard-stat-count">{stat.count}</span>
              <span className="dashboard-stat-label">{stat.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="heat-profile-content-wrapper dashboard-heat-facet-scope">
      {/* Search + Sort Bar */}
      <div className="dashboard-toolbar">
        <div className="dashboard-search-wrapper">
          <Search size={16} className="dashboard-search-icon" />
          <input
            type="text"
            className="dashboard-search-input"
            placeholder="Search projects by name, ID, location, manager..."
            value={searchText}
            onChange={searchData}
          />
        </div>
        <div className="dashboard-toolbar-right">
          <span className="dashboard-result-count">{sortedData.length} projects</span>
        </div>
      </div>

      <div className="dashboard-table-shell">
        <div className="dashboard-table-hscroll">
          <div className="dashboard-table-hscroll-inner">
      {/* Project List Header */}
      <div className="dashboard-list-header">
        <div className="dashboard-col-name dashboard-list-header-cell">
          {renderHeatColumnHeader('PROJECT NAME', 'projectName', 'projectName')}
        </div>
        <div className="dashboard-col-status dashboard-list-header-cell">
          {renderHeatColumnHeader('STATUS', '__statusLabel', 'projectStatus')}
        </div>
        <div className="dashboard-col-location dashboard-list-header-cell">
          {renderHeatColumnHeader('LOCATION', 'location', 'location')}
        </div>
        <div className="dashboard-col-type dashboard-list-header-cell">
          {renderHeatColumnHeader('PROJECT TYPE', 'projectType', 'projectType')}
        </div>
        <div className="dashboard-col-id dashboard-list-header-cell">
          {renderHeatColumnHeader('PROJECT ID', 'projectId', 'projectId')}
        </div>
        <div className="dashboard-col-client dashboard-list-header-cell">
          {renderHeatColumnHeader('CLIENT', 'clientName', 'clientName')}
        </div>
        <div className="dashboard-col-manager dashboard-list-header-cell">
          {renderHeatColumnHeader('MANAGER', 'manager', 'manager')}
        </div>
        <div className="dashboard-col-date dashboard-list-header-cell">
          {renderHeatColumnHeader('START DATE', '__startDisplay', 'startDate')}
        </div>
        <div className="dashboard-col-date dashboard-list-header-cell">
          {renderHeatColumnHeader('END DATE', '__endDisplay', 'endDate')}
        </div>
        <div className="dashboard-col-actions dashboard-list-header-cell dashboard-list-header-cell--static">
          ACTIONS
        </div>
      </div>

      {/* Project Cards List */}
      <div className="dashboard-project-list">
        {sortedData.length === 0 ? (
          <div className="dashboard-empty-state">
            <FolderOpen size={48} strokeWidth={1} />
            <p>No projects found</p>
          </div>
        ) : (
          sortedData.map((project, index) => {
            const status = getStatusString(project.projectStatus);
            const colors = statusColors[status] || statusColors.Unknown;
            return (
              <div
                key={project.id || index}
                className={`dashboard-project-card${listSelectedProjectId === project.id ? ' is-selected' : ''}`}
                onClick={() => handleProjectClick(project)}
              >
                <div className="dashboard-col-name">
                  <div
                    className="dashboard-project-avatar"
                    style={{ backgroundColor: colors.border }}
                  >
                    {project.projectName?.[0]?.toUpperCase() || '?'}
                  </div>
                  <span className="dashboard-project-name" title={project.projectName}>
                    {project.projectName}
                  </span>
                </div>
                <div className="dashboard-col-status">
                  <span
                    className="dashboard-status-tag"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `1px solid ${colors.border}40`,
                    }}
                  >
                    {status}
                  </span>
                </div>
                <div className="dashboard-col-location">
                  <span className="dashboard-cell-text">{project.location || '-'}</span>
                </div>
                <div className="dashboard-col-type">
                  <span
                    className="dashboard-cell-text"
                    title={getProjectTypeLabel(project) || undefined}
                  >
                    {getProjectTypeLabel(project) || '-'}
                  </span>
                </div>
                <div className="dashboard-col-id">
                  <span className="dashboard-cell-text">{project.projectId || '-'}</span>
                </div>
                <div className="dashboard-col-client">
                  <span className="dashboard-cell-text">{project.clientName || '-'}</span>
                </div>
                <div className="dashboard-col-manager">
                  <span className="dashboard-cell-text">{project.manager || '-'}</span>
                </div>
                <div className="dashboard-col-date">
                  <span className="dashboard-cell-text">
                    {formatProjectListDate(project.startDate, { empty: '-' })}
                  </span>
                </div>
                <div className="dashboard-col-date">
                  <span className="dashboard-cell-text">
                    {formatProjectListDate(project.endDate, { empty: '-' })}
                  </span>
                </div>
                <div className="dashboard-col-actions">
                  <button
                    className="dashboard-edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      showModal(project);
                    }}
                    title="Edit Project"
                  >
                    <Pencil size={15} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
          </div>
        </div>
      </div>
      </div>

      {/* Edit Modal - utilizing Custom DOM with Antd Inputs for strict styling boundary */}
      {isModalVisible && (
        <div className="dashboard-modal-overlay" onClick={handleCancel}>
          <div className="dashboard-modal" style={{ width: '55vw' }} onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-modal-header">
              <h2>Edit Project: {projectName}</h2>
              <button className="dashboard-modal-close" onClick={handleCancel}>×</button>
            </div>
            <div className="dashboard-modal-body">
              <Form form={projectForm} layout="vertical">
              <div className="dashboard-modal-grid">
                <div className="dashboard-modal-field">
                  <label>Project Name <span style={{ color: 'red' }}>*</span></label>
                  <Form.Item
                    name="projectName"
                    style={{ margin: 0 }}
                    rules={[{ required: true, message: 'Project Name is required' }]}
                  >
                    <Input size="large" placeholder="Enter Project Name" />
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Project ID</label>
                  <Form.Item name="projectId" style={{ margin: 0 }}>
                    <Input size="large" disabled />
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Client / Contractor <span style={{ color: 'red' }}>*</span></label>
                  <Form.Item name="clientName" style={{ margin: 0 }}>
                    <Select
                      className="dashboard-edit-select"
                      dropdownClassName="modern-dropdown-popup"
                      popupClassName="modern-dropdown-popup"
                      placeholder="Select Client / Contractor"
                      size="large"
                      style={{ width: '100%' }}
                    >
                      {clientData && clientData.map((item, i) => (
                        <Select.Option key={i} value={item?.name}>{item?.name}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Company</label>
                  <Form.Item name="finalClient" style={{ margin: 0 }}>
                    <Select
                      className="dashboard-edit-select"
                      dropdownClassName="modern-dropdown-popup"
                      popupClassName="modern-dropdown-popup"
                      placeholder="Select Company"
                      size="large"
                      style={{ width: '100%' }}
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      value={editFinalClient || undefined}
                      onChange={(v) => setEditFinalClient(v)}
                      filterOption={(input, option) =>
                        String(option?.children ?? '')
                          .toLowerCase()
                          .includes(String(input).toLowerCase())
                      }
                    >
                      {(clientData || []).map((item, i) => (
                        <Select.Option key={i} value={item && item.name}>
                          {item && item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Sub-Contractor</label>
                  <Form.Item
                    name="subcontractor"
                    style={{ margin: 0 }}
                  >
                    <Input size="large" placeholder="Sub-Contractor" />
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Offshore / Onshore <span style={{ color: 'red' }}>*</span></label>
                  <Form.Item
                    name="projectSiteType"
                    style={{ margin: 0 }}
                    rules={[{ required: true, message: 'Offshore / Onshore is required' }]}
                  >
                    <Select
                      className="dashboard-edit-select"
                      dropdownClassName="modern-dropdown-popup"
                      popupClassName="modern-dropdown-popup"
                      placeholder="Select Offshore or Onshore"
                      size="large"
                      style={{ width: '100%' }}
                      value={editProjectSiteType}
                      onChange={(v) => setEditProjectSiteType(v)}
                    >
                      <Select.Option value="Offshore">Offshore</Select.Option>
                      <Select.Option value="Onshore">Onshore</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>{editSiteLocationLabel} <span style={{ color: 'red' }}>*</span></label>
                  <Form.Item
                    name="siteLocationDetail"
                    style={{ margin: 0 }}
                    rules={[{ required: true, message: `${editSiteLocationLabel} is required` }]}
                  >
                    <Input size="large" placeholder={editSiteLocationLabel} />
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Country <span style={{ color: 'red' }}>*</span></label>
                  <Form.Item
                    name="location"
                    style={{ margin: 0 }}
                    rules={[{ required: true, message: 'Country is required' }]}
                  >
                    <Input size="large" placeholder="Country" />
                  </Form.Item>
                </div>

                <div className="dashboard-modal-field">
                  <label>CRCE Manager <span style={{ color: 'red' }}>*</span></label>
                  <Form.Item name="manager" style={{ margin: 0 }}>
                    <Input 
                      size="large" 
                      placeholder="CRCE Manager" 
                    />
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>CRCE Representative <span style={{ color: 'red' }}>*</span></label>
                  <Form.Item
                    name="crceRepresentative"
                    style={{ margin: 0 }}
                    rules={[{ required: true, message: 'CRCE Representative is required' }]}
                  >
                    <Input size="large" placeholder="On-board / on-site representative" />
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Initial Start Date <span style={{ color: 'red' }}>*</span></label>
                  <Form.Item
                    name="startDate"
                    style={{ margin: 0 }}
                    rules={[{ required: true, message: 'Initial Start Date is required' }]}
                  >
                    <DatePicker
                      allowClear={false}
                      format="DD-MM-YYYY"
                      dropdownClassName="modern-dropdown-popup"
                      popupClassName="modern-dropdown-popup"
                      style={{ width: '100%' }}
                      size="large"
                    />
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Initial End Date</label>
                  <Form.Item name="endDate" style={{ margin: 0 }}>
                    <DatePicker
                      format="DD-MM-YYYY"
                      allowClear={false}
                      dropdownClassName="modern-dropdown-popup"
                      popupClassName="modern-dropdown-popup"
                      style={{ width: '100%' }}
                      size="large"
                    />
                  </Form.Item>
                </div>

                <div className="dashboard-modal-field">
                  <label>Project Status</label>
                  <Form.Item name="projectStatus" style={{ margin: 0 }}>
                    <Select
                      className="dashboard-edit-select"
                      dropdownClassName="modern-dropdown-popup"
                      popupClassName="modern-dropdown-popup"
                      size="large"
                      style={{ width: '100%' }}
                    >
                      <Select.Option value={1}>Initiated</Select.Option>
                      <Select.Option value={2}>Active</Select.Option>
                      <Select.Option value={3}>On Hold</Select.Option>
                      <Select.Option value={4}>Completed</Select.Option>
                      <Select.Option value={5}>Facility</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Completed Welds</label>
                  <Form.Item name="completedWelds" style={{ margin: 0 }}>
                    <Input size="large" disabled />
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Technician Log Default Day/Night Shift</label>
                  <Form.Item name="technicianLog" style={{ margin: 0 }}>
                    <Input
                      size="large"
                      placeholder="0 Hour / 0 Hour"
                      onChange={(e) => {
                        const val = e.target.value;
                        projectForm.setFieldsValue({ technicianLog: val });
                        const parts = val.split('/');
                        if (parts.length >= 2) {
                          setDayShiftInputHour(parseInt(parts[0]) || 0);
                          setNightShiftInputHour(parseInt(parts[1]) || 0);
                        }
                      }}
                    />
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>Project Type <span style={{ color: 'red' }}>*</span></label>
                  <Form.Item
                    name="projectType"
                    style={{ margin: 0 }}
                    rules={[{ required: true, message: 'Project Type is required' }]}
                  >
                    <Select
                      className="dashboard-edit-select"
                      dropdownClassName="modern-dropdown-popup"
                      popupClassName="modern-dropdown-popup"
                      placeholder="Select project type"
                      size="large"
                      style={{ width: '100%' }}
                      value={editProjectType || undefined}
                      onChange={(v) => setEditProjectType(v)}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        String(option?.children ?? '')
                          .toLowerCase()
                          .includes(String(input).toLowerCase())
                      }
                    >
                      <Select.Option value="Welding">Welding</Select.Option>
                      <Select.Option value="Coating">Coating</Select.Option>
                      <Select.Option value="Both">Both</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
                <div className="dashboard-modal-field">
                  <label>NDT Type</label>
                  <Form.Item name="ndtType" style={{ margin: 0 }}>
                    <ModernMultiCheckboxSelect
                      value={editNdtType || undefined}
                      onChange={(v) => setEditNdtType(v)}
                      options={NDT_PIPELINE_TYPE_OPTIONS}
                      placeholder="Select NDT type(s)"
                    />
                  </Form.Item>
                </div>
                {(editProjectType === 'Welding' || editProjectType === 'Both') && (
                  <div className="dashboard-modal-field">
                    <label>Max NDT reject % (client limit)</label>
                    <Form.Item name="ndtRejectMaxPercent" style={{ margin: 0 }}>
                      <InputNumber
                        min={0}
                        max={100}
                        step={0.1}
                        controls={false}
                        placeholder="e.g. 2"
                        style={{ width: '100%' }}
                        size="large"
                      />
                    </Form.Item>
                  </div>
                )}
              </div>
              </Form>
            </div>
            <div className="dashboard-modal-footer">
              <CrceButton
                label="Cancel"
                variant="secondary"
                size="medium"
                onClick={handleCancel}
              />
              <CrceButton
                label="Save Changes"
                variant="primary"
                size="medium"
                onClick={handleSave}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ projectRequests, clientRequest, station, testReducer, equip }) => {
  const projectData = get(projectRequests, 'projectData', []);
  const clientData = get(clientRequest, 'clientData', []);
  const showProjectLoader = get(projectRequests, 'isProjectLoading', false);
  const stationData = get(station, 'stationData', []);
  const testTypes = get(testReducer, 'testTypes', []);
  const equipList = get(equip, 'equipList', []);
  const projectTeamMembersAccessList = get(equip, 'projectTeamMembersAccessList', []);
  return { projectData, showProjectLoader, clientData, stationData, testTypes, equipList, projectTeamMembersAccessList };
};

const mapDispatchToProps = {
  onGetProjects: fetchProject,
  onGetClients: fetchClients,
  onGetStations: fetchStationDetails,
  onGetTestValueTypes: fetchTestTypes,
  onFetchEquipList: fetchEquipList,
  onFetchTeamMembersAccessForProject: fetchTeamMembersAccessForProject,
  onSubmitProject: submitProject,
  onUpdateProject: updateProject,
  onSignOut: signOut,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardPage);
